// Node status for the builder tree + roll-up to parents.
// Plain JS so node can run treeState.selfcheck.mjs directly.

/** Worst-first. A single ⚠ deep in the tree must show on every ancestor. */
export const STATUS_ORDER = ['attention', 'empty', 'paused', 'done'];

export function worstStatus(statuses) {
  for (const s of STATUS_ORDER) if (statuses.includes(s)) return s;
  return 'done';
}

/**
 * Walks a node tree and returns {id: status}. A parent's own status is the
 * worst of its children; leaves use their `status` (default 'empty').
 * A paused parent stays paused unless a child needs attention.
 */
export function rollUp(nodes, out = {}) {
  for (const node of nodes) {
    if (node.children?.length) {
      rollUp(node.children, out);
      const childStatuses = node.children.map((c) => out[c.id]);
      const rolled = worstStatus(childStatuses);
      out[node.id] = node.status === 'paused' && rolled !== 'attention' ? 'paused' : rolled;
    } else {
      // an optional section left empty is fine: show it as done, not as a gap
      const status = node.status || 'empty';
      out[node.id] = node.need === 'optional' && status === 'empty' ? 'done' : status;
    }
  }
  return out;
}

/** "2 of 3 done" for a step/group. */
export function doneCount(nodes) {
  const leaves = flatten(nodes).filter((n) => !n.children?.length);
  return { done: leaves.filter((n) => n.status === 'done').length, total: leaves.length };
}

export function flatten(nodes, acc = []) {
  for (const n of nodes) {
    acc.push(n);
    if (n.children?.length) flatten(n.children, acc);
  }
  return acc;
}

/** Visible rows, honouring which parents are expanded — used for ↑↓ keys. */
export function visibleRows(nodes, expanded, depth = 0, acc = []) {
  for (const n of nodes) {
    acc.push({ ...n, depth });
    if (n.children?.length && expanded[n.id]) visibleRows(n.children, expanded, depth + 1, acc);
  }
  return acc;
}
